import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Text,
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import firebase from 'firebase';
import { useEffect, useMemo, useState } from 'react';
import * as zod from 'zod';
import api from '../../api';
import SetPasswordForm from '../../components/others/forms/SetPasswordForm';
import SplashScreen from '../../components/others/SplashScreen';
import { LangKey } from '../../intl';
import fb from '../../services/firebase.service';
import { useIntl } from '../../state/intl';
import useErrors from '../../utils/hooks/errors.hooks';
import useSnacks from '../../utils/hooks/snacks.hooks';
import getQueryParam from '../../utils/query-param.utils';
import Copyright from '../landing/Copyright';
import EmailAddressForm from './EmailAddressForm';

interface SignUpPageProps {}

const SignUpPage: React.FC<SignUpPageProps> = () => {
  const handleErr = useErrors();
  const { lang } = useIntl();
  const { showSuccess, showWarning } = useSnacks();

  const [isFormLoading, setFormLoading] = useState(false);
  const [state, setState] = useState<
    'initial' | 'must-request-link' | 'must-set-psw' | 'valid-link' | 'invalid-link' | 'email-sent'
  >('initial');

  const email = useMemo(() => {
    const value = getQueryParam('email');
    const result = zod.string().email().safeParse(value);

    return result.success ? result.data : '';
  }, []);

  useEffect(() => {
    const handleLink = async () => {
      let isLinkValid = false;

      if (email) {
        try {
          await fb.auth().signInWithEmailLink(email, window.location.href);
          isLinkValid = true;
        } catch (err) {
          console.error(err);
        }
      }

      setState(isLinkValid ? 'valid-link' : 'invalid-link');
    };

    const getAuthUser = (): Promise<firebase.User | null> => {
      return new Promise((res) => {
        // eslint-disable-next-line prefer-const
        let unsubscribe: () => void;

        const onChange = (user: firebase.User | null) => {
          if (unsubscribe) {
            unsubscribe();
          }
          res(user);
        };

        unsubscribe = fb.auth().onAuthStateChanged(onChange);
      });
    };

    const onMount = async () => {
      const currentUser = await getAuthUser();
      const isSignInLink = fb.auth().isSignInWithEmailLink(window.location.href);

      if (currentUser) {
        if (isSignInLink && email && currentUser.email !== email) {
          await fb.auth().signOut();
          handleLink();
          return;
        }

        const methods = await fb.auth().fetchSignInMethodsForEmail(currentUser.email!);
        if (methods.length === 1 && methods[0] === 'emailLink') {
          setState('must-set-psw');
        } else {
          window.location.replace('/');
        }
      } else {
        if (isSignInLink) {
          handleLink();
        } else {
          setState('must-request-link');
        }
      }
    };

    onMount();
  }, [email]);

  async function onSubmitSetPassword(password: string) {
    setFormLoading(true);

    try {
      await fb.auth().currentUser!.updatePassword(password);
      showSuccess();
      window.location.replace('/');
    } catch (err) {
      setFormLoading(false);

      if (err.code === 'auth/weak-password') {
        showWarning('Please provide a stronger password');
      } else {
        setState('invalid-link');
      }
    }
  }

  const onSubmitSendEmail = async (email: string) => {
    setFormLoading(true);

    try {
      await api.request('auth/requestSignUpEmail', { email });
      setState('email-sent');
    } catch (e) {
      const err = e as AxiosError;
      setFormLoading(false);
      if (err.response?.status === 404) {
        showWarning(tx.emailNotFound);
      } else {
        handleErr(err);
      }
    }
  };

  const tx = txs[lang];

  if (state === 'initial') {
    return <SplashScreen />;
  }

  const renderEmailAddressForm = () => (
    <EmailAddressForm initialEmail={email} isLoading={isFormLoading} onSubmit={onSubmitSendEmail} />
  );

  const renderEmailSentAlert = () => (
    <Alert
      status="success"
      variant="subtle"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      height="200px"
      mt={4}
      py={3}
      px={5}
      mb={3}
    >
      <AlertIcon boxSize="34px" mr={0} />
      <AlertTitle mt={5} mb={1} fontSize="lg">
        {tx.emailSentAlert.title}
      </AlertTitle>
      <AlertDescription maxWidth="sm">{tx.emailSentAlert.message}</AlertDescription>
    </Alert>
  );

  const renderInvalidLinkAlert = () => (
    <Alert status="warning" mb={2} mt={3} p={4}>
      <AlertIcon mr={4} />
      <Box flex="1">
        <AlertTitle>{tx.invalidLinkAlert.title}</AlertTitle>
        <AlertDescription display="block">{tx.invalidLinkAlert.message}</AlertDescription>
      </Box>
    </Alert>
  );

  const renderSetPasswordForm = () => (
    <SetPasswordForm loading={isFormLoading} handleSubmit={onSubmitSetPassword} isReset={false} />
  );

  return (
    <Center h="100%" flexDirection="column">
      <Center flexDirection="column" w="400px">
        <Text mb={2} fontWeight="700" fontSize={24}>
          {'Kolumboos PMS'}
        </Text>

        <Text fontSize={17} color="gray.400" mb={3}>
          {state === 'valid-link' || state === 'must-set-psw' ? tx.choosePsw : tx.requestPsw}
        </Text>

        {state === 'email-sent' && renderEmailSentAlert()}

        {(state === 'valid-link' || state === 'must-set-psw') && renderSetPasswordForm()}

        {state === 'invalid-link' && (
          <>
            {renderInvalidLinkAlert()}
            {renderEmailAddressForm()}
          </>
        )}

        {state === 'must-request-link' && (
          <>
            <Alert status="info" mb={2} mt={3} p={4}>
              <AlertIcon mr={3} />
              {tx.provideYourEmail}
            </Alert>

            {renderEmailAddressForm()}
          </>
        )}
      </Center>

      {(state === 'valid-link' || state === 'invalid-link') && (
        <Button variant="link" mt={7} onClick={() => window.location.replace('/')}>
          {tx.alreadyHavePsw}
        </Button>
      )}

      <Copyright />
    </Center>
  );
};

const txs: {
  [key in LangKey]: {
    emailSentAlert: {
      title: string;
      message: string;
    };
    invalidLinkAlert: {
      title: string;
      message: string;
    };
    choosePsw: string;
    requestPsw: string;
    provideYourEmail: string;
    alreadyHavePsw: string;
    emailNotFound: string;
  };
} = {
  en: {
    emailSentAlert: {
      title: 'Email successfully sent!',
      message:
        'You will receive a brand new link to set your password in minutes. Check your inbox!',
    },
    invalidLinkAlert: {
      title: 'This link is expired',
      message: 'Please provide your email address and we will send you a new valid one.',
    },
    choosePsw: 'Choose your password',
    requestPsw: 'Request your password',
    provideYourEmail:
      'Provide your email address and we will send you a link to set your password.',
    alreadyHavePsw: 'I already have a password',
    emailNotFound: 'This email does not belong to any user',
  },
  it: {
    emailSentAlert: {
      title: 'Email inviata con successo!',
      message:
        'Riceverai a momenti un nuovo link per impostare la tua password. Controlla la tua casella di posta!',
    },
    invalidLinkAlert: {
      title: 'Questo link è scaduto',
      message: 'Inserisci il tuo indirizzo email e te ne manderemo uno nuovo e funzionante.',
    },
    choosePsw: 'Scegli la tua password',
    requestPsw: 'Richiedi la tua password',
    provideYourEmail:
      'Inserisci il tuo indirizzo email e ti manderemo un link per impostare la tua password.',
    alreadyHavePsw: 'Ho già una password',
    emailNotFound: 'Questa email non è associata ad alcun utente',
  },
  es: {
    emailSentAlert: {
      title: 'Email successfully sent!',
      message:
        'You will receive a brand new link to set your password in minutes. Check your inbox!',
    },
    invalidLinkAlert: {
      title: 'This link is expired',
      message: 'Please provide your email address and we will send you a new valid one.',
    },
    choosePsw: 'Choose your password',
    requestPsw: 'Request your password',
    provideYourEmail:
      'Provide your email address and we will send you a link to set your password.',
    alreadyHavePsw: 'I already have a password',
    emailNotFound: 'This email does not belong to any user',
  },
};

export default SignUpPage;
